import React, { useEffect } from 'react';
import { OG_IMAGE_URL } from './config';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { LeadProvider } from './contexts/LeadContext';
import { usePageTracking } from './hooks/usePageTracking';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { PainPoints } from './components/PainPoints';
import { MeetTheQueens } from './components/MeetTheQueens';
import { QueenExperience } from './components/QueenExperience';
import { MostWanted } from './components/MostWanted';
import { TradeIn } from './components/TradeIn';
import { VideoSection } from './components/VideoSection';
import { LeadForm } from './components/LeadForm/LeadForm';
import { HIDE_LEAD_FORM_FOR_COMPLIANCE } from './config';
import { HowItWorks } from './components/HowItWorks';
import { Faq } from './components/Faq';
import { Footer } from './components/Footer';
import { SectionDivider } from './components/SectionDivider';
import { GHLWidget } from './components/GHLWidget';
import { PrivacyPolicy } from './components/PrivacyPolicy';
import { BrowserRouter, Routes, Route } from 'react-router-dom';

/**
 * Head tags.
 *
 * robots: REMOVE BEFORE LAUNCH — placeholder content is currently live, so the page
 * must not be indexed yet.
 *
 * og:image: rendered only once OG_IMAGE_URL holds a real approved asset. Emitting a tag
 * that points nowhere makes WhatsApp and Meta render a broken preview card.
 */
function useHeadMeta() {
  useEffect(() => {
    const tags: HTMLMetaElement[] = [];
    const add = (attr: 'name' | 'property', key: string, content: string) => {
      const meta = document.createElement('meta');
      meta.setAttribute(attr, key);
      meta.content = content;
      document.head.appendChild(meta);
      tags.push(meta);
    };

    // REMOVE BEFORE LAUNCH
    add('name', 'robots', 'noindex, nofollow');
    if (OG_IMAGE_URL) {
      add('property', 'og:image', OG_IMAGE_URL);
      add('name', 'twitter:image', OG_IMAGE_URL);
      add('name', 'twitter:card', 'summary_large_image');
    }
    return () => {
      tags.forEach(tag => document.head.removeChild(tag));
    };
  }, []);
}
function Landing() {
  const {
    t
  } = useLanguage();
  usePageTracking();
  useHeadMeta();
  return <div className="w-full overflow-x-hidden bg-white" data-id="element-0" data-mp="d2l6qzzotwz9">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[70] focus:bg-ink focus:px-4 focus:py-2 focus:text-sm focus:font-bold focus:text-white" data-id="element-1" data-mp="86mg15qw02vo">
        {t('nav.skip')}
      </a>
      <Header data-id="element-2" data-mp="3qb7j1d7if6g9" />
      <main id="main" data-id="element-3" data-mp="3qaxu7sy7b5c5">
        {/* One argument per screen, each ending in the next step:
            hook → who they are → why different → what to pick → your car →
            what happens next → give your data → objections handled. */}
        <VideoSection src="https://cdn.magicpatterns.com/uploads/d5VZi8boURW3RqnLtkGKAS/A_REAL_SUV_15_1920x796.mp4" fullBleed priority data-id="element-4" data-mp="2xtlnt8e6uigy" />
        <Hero data-id="element-5" data-mp="18dhpvydbqq3d" />
        <PainPoints data-id="element-6" data-mp="18czlufkf77kw" />
        <MeetTheQueens data-id="element-7" data-mp="18cgh60js6fot" />
        <SectionDivider data-id="element-8" data-mp="1d4hnl5u9pc6x" />
        <QueenExperience data-id="element-9" data-mp="1d4zuqxigxatm" />
        <MostWanted data-id="element-10" data-mp="1d59j6gnv79x7" />
        <TradeIn data-id="element-11" data-mp="1d5su3df3ky1q" />
        <HowItWorks data-id="element-12" data-mp="1d6at2ht59qdc" />
        <VideoSection src="https://cdn.magicpatterns.com/uploads/xyHVoiyZwgDdy7V9eYcTtp/my27-telli-ice-hero-desktop_moov.mp4" data-id="element-13" data-mp="yg3nv3s5e3ul" />
        {!HIDE_LEAD_FORM_FOR_COMPLIANCE && <LeadForm data-id="element-14" data-mp="13clpvvw3fo38" />}
        <SectionDivider data-id="element-15" data-mp="1r4971m3jzksb" />
        <Faq data-id="element-16" data-mp="1r3zj0422jnp9" />
      </main>
      <Footer data-id="element-17" data-mp="1r4sgeef510uq" />
      <GHLWidget data-id="element-18" data-mp="1r529wbze5q56" />
    </div>;
}
export function App() {
  return <BrowserRouter data-id="element-19" data-mp="1w20zqic0vaxk">
      <LanguageProvider data-id="element-20" data-mp="1w2ki205l41c4">
        <LeadProvider data-id="element-21" data-mp="1w0zl63vss6b3">
          <Routes data-id="element-22" data-mp="1i442djpqv0hu">
            <Route path="/" element={<Landing data-id="element-24" data-mp="1i3b2n2p7db97" />} data-id="element-23" data-mp="1i2j7tu3fjhjz" />
            <Route path="/privacy" element={<PrivacyPolicy data-id="element-26" data-mp="1m4ei3o3c959w" />} data-id="element-25" data-mp="1i5xcj8zkg2ul" />
          </Routes>
        </LeadProvider>
      </LanguageProvider>
    </BrowserRouter>;
}